import React, { Component } from "react";
import axios from "axios";

import AppRouter from "./AppRouter.js";
import { BACKEND_CONFIG, LOGIN_URL } from "./config/consts";
import { SpinnerProvider, SpinnerConsumer } from "./context/spinner-context";
import { loadScript } from "./utils/actions";

import "./styles/app.scss";

class App extends Component {
  requestCount = 0;
  isRefreshing = false;
  failedQueue = [];

  constructor() {
    super();

    this.state = { isSpinnerShown: false };

    this.setupInterceptors();
  }

  processQueue(error, token = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    this.failedQueue = [];
  }

  componentDidMount() {
    axios
      .get(`${BACKEND_CONFIG.apiUrl}account/environment-mode`)
      .then((response) => {
        if (response.data.IsProductionMode) {
          loadScript(
            "//rum-static.pingdom.net/pa-5c5b06359a3f83001600062e.js",
            true
          );
        }
      });
  }

  render() {
    return (
      <SpinnerProvider value={this.state}>
        <SpinnerConsumer>
          {(context) => {
            this.showSpinner = context.showSpinner;
          }}
        </SpinnerConsumer>
        <AppRouter />
      </SpinnerProvider>
    );
  }

  setupInterceptors() {
    var self = this;

    axios.interceptors.request.use(
      function (config) {
        self.setSpinner(true);

        if (config.url === LOGIN_URL) {
          return config;
        }

        if (BACKEND_CONFIG.get("auth")) {
          config.headers.common["Authorization"] = `Bearer ${BACKEND_CONFIG.get(
            "auth"
          )}`;

          var agencyId = BACKEND_CONFIG.get("agencyid");

          if (agencyId) {
            config.headers.common["X-AgencyId"] = agencyId;
          }

          return config;
        }

        return config;
      },
      (error) => {
        self.setSpinner(false);
        this.showError(error);

        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      function (config) {
        self.setSpinner(false);

        return config;
      },
      (error) => {
        const originalRequest = error.config;

        self.setSpinner(false);

        if (error && error.response) {
          if (error.response.status === 401) {
            if (originalRequest.url.endsWith("/refresh")) {
              self.logOut();
              window.location.href = "/login";

              return Promise.reject(error);
            } else if (!originalRequest._retry) {
              // If refreshing the token then send request to a queue
              if (self.isRefreshing) {
                return new Promise((resolve, reject) => {
                  self.failedQueue.push({ resolve, reject });
                })
                  .then(() => {
                    originalRequest.headers[
                      "Authorization"
                    ] = `Bearer ${BACKEND_CONFIG.get("auth")}`;

                    return axios(originalRequest);
                  })
                  .catch((err) => err);
              }

              // If header of the request has changed, it means token was refreshed
              if (
                originalRequest.headers["Authorization"] !==
                `Bearer ${BACKEND_CONFIG.get("auth")}`
              ) {
                originalRequest.headers[
                  "Authorization"
                ] = `Bearer ${BACKEND_CONFIG.get("auth")}`;

                return Promise.resolve(axios(originalRequest));
              }

              originalRequest._retry = true;
              self.isRefreshing = true;

              const refreshToken = BACKEND_CONFIG.get("refresh");

              return new Promise((resolve, reject) => {
                axios
                  .post(`${BACKEND_CONFIG.apiUrl}account/login/refresh`, {
                    token: refreshToken,
                  })
                  .then((res) => {
                    if (res.status === 200) {
                      self.isRefreshing = false;
                      //update token
                      BACKEND_CONFIG.remove("auth");
                      BACKEND_CONFIG.remove("refresh");
                      BACKEND_CONFIG.set("auth", res.data.Token);
                      BACKEND_CONFIG.set("refresh", res.data.RefreshToken);

                      //resolve queued
                      self.processQueue(null, res.data.Token);

                      //resolve current
                      originalRequest.headers[
                        "Authorization"
                      ] = `Bearer ${res.data.Token}`;

                      resolve(axios(originalRequest));
                    } else {
                      self.logOut();

                      window.location.href = "/login";

                      reject(error);
                    }
                  })
                  .catch((err) => {
                    self.processQueue(err, null);
                    reject(err);

                    var route = this.tryExternalLogin();

                    if (!route || route === "/login") {
                      self.logOut();
                    }

                    window.location.href = route ? route : "/login";
                  });
              });
            }
          } else if (error.response.status === 503) {
            window.location.href = "/error-503";

            return Promise.reject(error);
          }
        }

        this.showError(error);

        return Promise.reject(error);
      }
    );
  }

  //refresh workaround
  tryExternalLogin() {
    axios
      .post(
        `${BACKEND_CONFIG.apiUrl}account/external-login`,
        {
          agencyId: BACKEND_CONFIG.get("agencyid"),
          token: BACKEND_CONFIG.get("auth"),
        },
        {
          headers: {
            Authorization: `Bearer ${BACKEND_CONFIG.get("auth")}`,
            Pragma: "no-cache",
          },
        }
      )
      .then((response) => {
        if (response.data.Agency) {
          BACKEND_CONFIG.clearAll();
          BACKEND_CONFIG.set("auth", response.data.Token);
          BACKEND_CONFIG.set("refresh", response.data.RefreshToken);
          BACKEND_CONFIG.set("agencyid", response.data.Agency.AgencyId);

          var clientId = BACKEND_CONFIG.get("clientid");

          return clientId ? `/client/${clientId}` : "/home";
        } else {
          return "/home";
        }
      })
      .catch((error) => {
        return "/login";
      });
  }

  logOut() {
    BACKEND_CONFIG.remove("auth");
    BACKEND_CONFIG.remove("refresh");
    BACKEND_CONFIG.remove("agencyid");
  }

  showError(error) {}

  setSpinner(toShow) {
    if (toShow) {
      this.requestCount++;

      if (this.requestCount === 1) {
        this.showSpinner(true);
      }
    } else {
      this.requestCount--;

      if (this.requestCount <= 0) {
        this.requestCount = 0;

        this.showSpinner(false);
      }
    }
  }
}

export default App;
